import { Workflow } from '@/stores/workflowStore';
import { brandedBuildStatus, getBrandedBlockDef } from '@/lib/brandedBlocks';
import {
  missionGoalBlocks,
  missionStartBlockedReason,
  missionStartBlocks,
} from '@/lib/missionRules';
import { PRODUCT_TERMS } from '@/lib/productTerms';

export interface ValidationError {
  type: 'error' | 'warning';
  message: string;
  nodeId?: string;
}

export class WorkflowValidator {
  static validate(workflow: Workflow): ValidationError[] {
    const errors: ValidationError[] = [];

    const startBlockReason = missionStartBlockedReason(workflow);
    if (startBlockReason) {
      errors.push({ type: 'error', message: startBlockReason });
    }

    const starts = missionStartBlocks(workflow);
    if (starts.length > 1) {
      starts.slice(1).forEach((node) => {
        errors.push({
          type: 'error',
          message: 'Remove extra START blocks — Mission requires exactly one',
          nodeId: node.id,
        });
      });
    }

    if (missionGoalBlocks(workflow).length === 0) {
      errors.push({
        type: 'warning',
        message: 'No GOAL block — add one with the identified outcome (recommended)',
      });
    }

    // Check for at least one trigger
    const triggers = workflow.nodes.filter((n) => n.type === 'trigger');
    if (triggers.length === 0) {
      errors.push({
        type: 'warning',
        message: 'No trigger blocks yet — AI or you can add steps between START and GOAL',
      });
    }

    // Check for at least one action
    const actions = workflow.nodes.filter((n) => n.type === 'action');
    if (actions.length === 0) {
      errors.push({
        type: 'warning',
        message: 'Workflow has no action nodes. No output will be produced.',
      });
    }

    // Check each node
    workflow.nodes.forEach((node) => {
      if (node.type === 'goal' && node.subtype === 'outcome') {
        const outcome = String(node.config.outcome ?? '').trim();
        if (!outcome) {
          errors.push({
            type: 'warning',
            message: 'GOAL Block has no identified outcome yet',
            nodeId: node.id,
          });
        }
      }

      if (node.type === 'agent') {
        const brief = String(node.config.agentBrief ?? node.config.contextPurpose ?? '').trim();
        if (!brief) {
          errors.push({
            type: 'warning',
            message: 'Agent Block needs a brief in contextual configuration',
            nodeId: node.id,
          });
        }
      }

      if (node.type === 'drone') {
        const task = String(node.config.droneTask ?? node.config.contextPurpose ?? '').trim();
        if (!task) {
          errors.push({
            type: 'warning',
            message: 'Drone Block needs a task in contextual configuration',
            nodeId: node.id,
          });
        }
      }

      if (node.type === 'branded') {
        const status = brandedBuildStatus(node.subtype, node.config);
        if (status === 'idea') {
          errors.push({
            type: 'warning',
            message: `${getBrandedBlockDef(node.subtype)?.shortLabel ?? 'Branded'} block is an idea — add clear purpose before run`,
            nodeId: node.id,
          });
        }
      }

      // Validate required configurations
      if (node.type === 'tool' && node.subtype === 'http') {
        if (!node.config.url) {
          errors.push({
            type: 'error',
            message: `HTTP Request node missing URL`,
            nodeId: node.id,
          });
        }
      }

      if (node.type === 'tool' && node.subtype === 'transform') {
        if (!node.config.transform) {
          errors.push({
            type: 'error',
            message: `Transform node missing transformation code`,
            nodeId: node.id,
          });
        }
      }

      if (node.type === 'action' && node.subtype === 'webhook') {
        if (!node.config.url) {
          errors.push({
            type: 'error',
            message: `Webhook node missing URL`,
            nodeId: node.id,
          });
        }
      }

      if (node.type === 'trigger' && node.subtype === 'gmail_inbound') {
        if (!node.config.inboxId || node.config.inboxId === 'pending-inbox') {
          errors.push({
            type: 'warning',
            message: `Gmail inbound trigger missing inbox ID`,
            nodeId: node.id,
          });
        }
      }

      if (node.type === 'trigger' && node.subtype === 'webhook') {
        if (!node.config.url) {
          errors.push({
            type: 'error',
            message: `Webhook trigger missing configuration`,
            nodeId: node.id,
          });
        }
      }
    });

    // Check for disconnected nodes
    workflow.nodes.forEach((node) => {
      const hasIncoming = workflow.edges.some((e) => e.to === node.id);
      const isStart = node.type === 'start';
      const isGoal = node.type === 'goal';
      const isTube = node.type === 'tube';

      if (!isStart && !isGoal && !isTube && !hasIncoming) {
        errors.push({
          type: 'warning',
          message: `Node "${node.subtype}" has no incoming connections`,
          nodeId: node.id,
        });
      }
    });

    // Check for circular dependencies
    if (this.hasCircularDependency(workflow)) {
      errors.push({
        type: 'error',
        message: 'Workflow has circular dependencies',
      });
    }

    return errors;
  }

  private static hasCircularDependency(workflow: Workflow): boolean {
    const visited = new Set<string>();
    const recursionStack = new Set<string>();

    const visit = (nodeId: string): boolean => {
      visited.add(nodeId);
      recursionStack.add(nodeId);

      const outgoing = workflow.edges.filter((e) => e.from === nodeId);
      for (const edge of outgoing) {
        if (!visited.has(edge.to)) {
          if (visit(edge.to)) return true;
        } else if (recursionStack.has(edge.to)) {
          return true;
        }
      }

      recursionStack.delete(nodeId);
      return false;
    };

    for (const node of workflow.nodes) {
      if (!visited.has(node.id)) {
        if (visit(node.id)) return true;
      }
    }

    return false;
  }
}
